import useSWR from 'swr'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// Lib
import {
    getServiceCaseStatus,
    getServiceCaseType,
    getHandlingLocation,
} from '@/lib/serviceCaseStatuses'

export default function useServiceCase({ serviceCaseId = null, onMutate = null } = {}) {
    const { requestGet, requestPatch, requestPost } = useAxiosApi()

    const { data: response, error, isLoading, mutate } = useSWR(
        serviceCaseId ? `/service-cases/${serviceCaseId}` : null,
        requestGet
    )

    const data = response?.data

    // --- Mutation helpers ---

    const updateServiceCase = async (fields) => {
        const result = await requestPatch(
            `/service-cases/${serviceCaseId}`,
            fields
        )
        await mutate()
        onMutate?.()
        return result
    }

    const performAction = async (action, actionData = {}, email = null) => {
        const body = { action, data: actionData }
        if (email) body.email = email
        const result = await requestPost(
            `/service-cases/${serviceCaseId}/action`,
            body
        )
        await mutate()
        onMutate?.()
        return result
    }

    // If no data, return early with loading state
    if (!data) {
        return {
            serviceCase: null,
            isLoading,
            error,
            reload: mutate,
            updateServiceCase,
            performAction,
        }
    }

    // Computed values
    const status = getServiceCaseStatus(data.status)
    const type = getServiceCaseType(data.type)
    const handlingLocation = data.is_handled_by
        ? getHandlingLocation(data.is_handled_by)
        : null

    const isArchived =
        data.is_archived === '1' || data.is_archived === 1

    const number =
        data.number || `BR${String(data.id).padStart(5, '0')}`

    // Format customer info string
    const customerInfoString = [
        data.customer_phone,
        data.customer_email,
    ]
        .filter(Boolean)
        .join(', ')

    // Build serviceCase object with all computed values
    const serviceCase = {
        // Raw data
        ...data,

        // Computed
        number,
        status_config: status,
        type_config: type,
        handling_location_config: handlingLocation,
        is_archived: isArchived,
        customer_info_string: customerInfoString,

        // Relations (already included in data)
        shipments: data.shipments || [],
    }

    return {
        serviceCase,
        isLoading,
        error,
        reload: mutate,
        updateServiceCase,
        performAction,
    }
}
